// src/components/Page.tsx

import React, { useState } from 'react'
import { Pencil, FileText, List, Plus, Trash2 } from 'lucide-react'
import MarkdownEditor from './MarkdownEditor'
import clsx from 'clsx'

type PageComposant = {
  id: string
  name: string
  description: string
  images?: string[]
}

export type PageData = {
  id: string
  name: string
  description: string
  images?: string[]
  local_components: PageComposant[]
}

type PageProps = {
  page: PageData
  onChange: (updated: PageData) => void
  onDelete?: () => void
  selected?: boolean
  className?: string
}

export const Page: React.FC<PageProps> = ({
  page,
  onChange,
  onDelete,
  selected = false,
  className = '',
}) => {
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleValue, setTitleValue] = useState(page.name)
  const [composantEditId, setComposantEditId] = useState<string | null>(null)
  const [addingComposant, setAddingComposant] = useState(false)
  const [newComposantName, setNewComposantName] = useState('')

  // Title edit logic
  const handleTitleBlur = () => {
    setEditingTitle(false)
    if (titleValue.trim() && titleValue !== page.name) {
      onChange({ ...page, name: titleValue.trim() })
    } else {
      setTitleValue(page.name)
    }
  }

  // Markdown content change
  const handleContentChange = (val: string) => {
    onChange({ ...page, description: val })
  }

  // Composant logic
  const handleAddComposant = () => {
    if (!newComposantName.trim()) return
    let v = newComposantName.trim();
    v = filterLettersOnly(v);
    v = capitalizeFirstLetter(v);
    const newComposant: PageComposant = {
      id: Math.random().toString(36).slice(2, 10),
      name: v,
      description: '',
    }
    onChange({ ...page, local_components: [...page.local_components, newComposant] })
    setNewComposantName('')
    setAddingComposant(false)
    setComposantEditId(newComposant.id)
  }

  const handleComposantDescChange = (id: string, desc: string) => {
    onChange({
      ...page,
      local_components: page.local_components.map((c) =>
        c.id === id ? { ...c, description: desc } : c,
      ),
    })
  }

  const handleComposantNameChange = (id: string, name: string) => {
    let v = name;
    v = filterLettersOnly(v);
    v = capitalizeFirstLetter(v);
    onChange({
      ...page,
      local_components: page.local_components.map((c) =>
        c.id === id ? { ...c, name: v } : c,
      ),
    })
  }

  const handleDeleteComposant = (id: string) => {
    onChange({
      ...page,
      local_components: page.local_components.filter((c) => c.id !== id),
    })
    if (composantEditId === id) setComposantEditId(null)
  }

  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = () => {
      const imageUrl = reader.result as string

      onChange(
        { ...page, images: [...(page.images || []), imageUrl] }
      )
    }
    reader.readAsDataURL(file)
  }

  const handleRemoveImage = (id: string, index: number) => {
    onChange(
      { ...page, images: (page.images || []).filter((_, i) => i !== index) }
    )
  }


  const handleComponentImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = () => {
      const imageUrl = reader.result as string

      onChange({
        ...page,
        local_components: page.local_components.map((c) =>
          c.id === id
            ? {
              ...c,
              images: [...(c.images || []), imageUrl],
            }
            : c,
        ),
      })
    }
    reader.readAsDataURL(file)
  }

  const handleComponentRemoveImage = (id: string, index: number) => {
    onChange({
      ...page,
      local_components: page.local_components.map((c) =>
        c.id === id
          ? {
            ...c,
            images: (c.images || []).filter((_, i) => i !== index),
          }
          : c,
      ),
    })
  }

  function filterLettersOnly(str) {
    return str.replace(/[^a-zA-Z]/g, '');
  }

  function capitalizeFirstLetter(str) {
    if (str.length === 0) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  return (
    <section
      className={clsx(
        'card p-4 md:p-6 flex flex-col gap-6 relative transition-shadow',
        selected && 'ring-2 ring-brixel shadow-lg',
        className,
      )}
      aria-label={`Page ${page.name}`}
    >
      {/* Title */}
      <div className="flex items-center gap-2">
        <FileText className="w-5 h-5 text-blue-brixel" />
        {editingTitle ? (
          <input
            className="font-bold text-lg md:text-xl border-b border-brixel/30 bg-transparent px-1 py-0.5 focus:outline-none focus:border-brixel transition w-full max-w-xs"
            value={titleValue}
            onChange={(e) => {
              let v = e.target.value;
              v = filterLettersOnly(v);
              v = capitalizeFirstLetter(v);
              setTitleValue(v);
            }}
            onBlur={handleTitleBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleTitleBlur();
              }
              if (e.key === 'Escape') {
                setEditingTitle(false);
                setTitleValue(page.name);
              }
            }}
            autoFocus
            maxLength={80}
            aria-label="Titre de la page"
          />
        ) : (
          <h2
            className={clsx(
              'font-bold text-lg md:text-xl flex-1 truncate',
              !page.name && 'italic text-gray-400',
            )}
            tabIndex={0}
            onClick={() => setEditingTitle(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setEditingTitle(true)
            }}
            role="button"
            aria-label="Modifier le titre"
            title="Cliquer pour éditer le titre"
          >
            {page.name || 'Titre de la page'}
          </h2>
        )}
        {!editingTitle && (
          <>
            <button
              type="button"
              className="p-1 rounded hover:bg-brixel/10 text-brixel"
              onClick={() => setEditingTitle(true)}
              title="Éditer le titre"
              aria-label="Éditer le titre"
            >
              <Pencil className="w-4 h-4" />
            </button>
            {onDelete && (
              <button
                type="button"
                className="p-1 rounded hover:bg-red-100 text-red-500"
                title="Supprimer la page"
                onClick={onDelete}
                aria-label="Supprimer la page"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </>

        )}
      </div>

      {/* Markdown editor */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
          <FileText className="w-4 h-4 text-blue-brixel" />
          Contenu de la page (Markdown)
        </label>
        <MarkdownEditor
          value={page.description}
          onChange={handleContentChange}
          placeholder="Écrivez le contenu principal de cette page…"
          minRows={8}
        />
        <div className="mt-4">
          <label className="text-sm font-medium text-gray-700">Images</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {(page.images || []).map((url, index) => (
              <div key={index} className="relative group">
                <img
                  src={url}
                  alt={`img-${index}`}
                  className="w-24 h-24 object-cover rounded border border-gray-200"
                />
                <button
                  type="button"
                  title="Supprimer"
                  onClick={() => handleRemoveImage(page.id, index)}
                  className="absolute top-1 right-1 bg-white/80 rounded-full p-1 shadow hover:bg-red-500 hover:text-white transition text-sm text-gray-600"
                >
                  ×
                </button>
              </div>
            ))}
            <label className="w-24 h-24 flex items-center justify-center border border-dashed border-gray-300 rounded cursor-pointer hover:bg-gray-100 text-sm text-gray-500">
              +
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => handleImageUpload(e, page.id)}
              />
            </label>
          </div>
        </div>
      </div>

      {/* Composants de la page */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <List className="w-5 h-5 text-yellow-brixel" />
          <span className="font-semibold text-gray-900">Composants de la page</span>
          <button
            type="button"
            className="ml-auto rounded bg-brixel/90 p-1.5 text-white hover:bg-brixel focus:outline-none focus:ring-2 focus:ring-brixel"
            title="Ajouter un composant"
            onClick={() => setAddingComposant(true)}
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
        {/* Liste des local_components */}
        {page.local_components.length === 0 && !addingComposant && (
          <div className="text-gray-400 italic text-sm px-2 py-1">
            Aucun composant pour cette page.
          </div>
        )}
        <ul className="space-y-3">
          {page.local_components.map((comp) => (
            <li
              key={comp.id}
              className={clsx(
                'rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 relative group',
                composantEditId === comp.id && 'ring-2 ring-brixel/30 bg-white',
              )}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-gray-800 flex-1">
                  <input
                    className="bg-transparent font-medium text-gray-800 px-0.5 py-0.5 border-b border-transparent focus:border-brixel/60 focus:outline-none rounded transition w-auto max-w-[160px]"
                    value={comp.name}
                    onChange={(e) =>
                      handleComposantNameChange(comp.id, e.target.value)
                    }
                    maxLength={40}
                    aria-label="Nom du composant"
                  />
                </span>
                <button
                  type="button"
                  className="p-1 rounded hover:bg-brixel/10 text-brixel"
                  title="Éditer la description"
                  onClick={() => setComposantEditId(comp.id)}
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  className="p-1 rounded hover:bg-red-100 text-red-500"
                  title="Supprimer le composant"
                  onClick={() => handleDeleteComposant(comp.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {/* Description markdown */}
              {composantEditId === comp.id ? (
                <div className="mt-1">
                  <MarkdownEditor
                    value={comp.description}
                    onChange={(desc) =>
                      handleComposantDescChange(comp.id, desc)
                    }
                    placeholder="Décrivez ce composant (Markdown)…"
                    minRows={3}
                    maxRows={10}
                    autoFocus
                  />
                  <div className="mt-4">
                    <label className="text-sm font-medium text-gray-700">Images</label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(comp.images || []).map((url, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={url}
                            alt={`img-${index}`}
                            className="w-24 h-24 object-cover rounded border border-gray-200"
                          />
                          <button
                            type="button"
                            title="Supprimer"
                            onClick={() => handleComponentRemoveImage(comp.id, index)}
                            className="absolute top-1 right-1 bg-white/80 rounded-full p-1 shadow hover:bg-red-500 hover:text-white transition text-sm text-gray-600"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <label className="w-24 h-24 flex items-center justify-center border border-dashed border-gray-300 rounded cursor-pointer hover:bg-gray-100 text-sm text-gray-500">
                        +
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={(e) => handleComponentImageUpload(e, comp.id)}
                        />
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      className="btn-outline flex-1"
                      onClick={() => setComposantEditId(null)}
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              ) : comp.description ? (
                <div className="markdown text-sm mt-1">{comp.description}</div>
              ) : (
                <div className="text-gray-400 italic text-xs mt-1">
                  Pas de description.
                </div>
              )}
            </li>
          ))}
        </ul>
        {/* Ajout d'un composant */}
        {addingComposant && (
          <div className="flex items-center gap-2 mt-3 w-full">
            <input
              className="border border-gray-200 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-brixel flex-1 min-w-0"
              placeholder="Nom du composant…"
              value={newComposantName}
              onChange={(e) => setNewComposantName(e.target.value)}
              maxLength={40}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddComposant()
                if (e.key === 'Escape') {
                  setAddingComposant(false)
                  setNewComposantName('')
                }
              }}
            />
            <button
              type="button"
              className="btn px-1 py-1 text-sm"
              onClick={handleAddComposant}
              disabled={!newComposantName.trim()}
            >
              Ajouter
            </button>
            <button
              type="button"
              className="btn-outline px-1 py-1 text-sm"
              onClick={() => {
                setAddingComposant(false)
                setNewComposantName('')
              }}
            >
              Annuler
            </button>
          </div>
        )}
      </div>
    </section>
  )
}

export default Page
